import React, { useEffect } from 'react';
import {
    Route,
    Routes,
    Navigate
} from 'react-router-dom';
import { useAuthContext } from 'context/AuthContext';
import SignIn from 'pages/SignIn';
import PasswordReset from 'pages/PasswordReset';
import Students from 'pages/Students';
import Student from 'pages/Student';
import BulkPrintReports from 'pages/BulkPrintReports';
import LatestScores from 'pages/LatestScores';
import Instructors from 'pages/Instructors';
import Instructor from 'pages/Instructor';
import Tests from 'pages/Tests';
import TestSATPreview from 'pages/TestSATPreview';
import TestACTPreview from 'pages/TestACTPreview';
import TestSHSATPreview from 'pages/TestSHSATPreview';
import TestPaperACTPreview from 'pages/TestPaperACTPreview';
import TestTSIA2Preview from 'pages/TestTSIA2Preview';
import TestSATReport from 'pages/TestSATReport';
import TestACTReport from 'pages/TestACTReport';
import TestSHSATReport from 'pages/TestSHSATReport';
import TestTSIA2Report from 'pages/TestTSIA2Report';
import TestPaperACTReport from 'pages/TestPaperACTReport';
import TestSATReview from 'pages/TestSATReview';
import TestACTReview from 'pages/TestACTReview';
import TestSHSATReview from 'pages/TestSHSATReview';
import TestTSIA2Review from 'pages/TestTSIA2Review';
import TestPaperACTReview from 'pages/TestPaperACTReview';
import UploadTest from 'pages/UploadTest';
import Assignments from 'pages/Assignments';
import CreateAssignment from 'pages/Assignments/CreateAssignment';
import AssignmentPreview from 'pages/AssignmentPreview';
import AssignmentReport from 'pages/AssignmentReport';
import AssignmentReview from 'pages/AssignmentReview';
import Classes from 'pages/Classes';
import Class from 'pages/Class';
import ClassSATReport from 'pages/ClassSATReport';
import ClassACTReport from 'pages/ClassACTReport';
import ClassSHSATReport from 'pages/ClassSHSATReport';
import ClassTSIA2Report from 'pages/ClassTSIA2Report';
import TestBCAPreview from 'pages/TestBCAPreview';
import TestBCAReport from 'pages/TestBCAReport';
import TestBCAReview from 'pages/TestBCAReview';
import ClassBCAReport from 'pages/ClassBCAReport';
import ClassPaperACTReport from 'pages/ClassPaperACTReport';
import ClassAssignmentReport from 'pages/ClassAssignmentReport';
import Profile from 'pages/Profile';
import Credits from 'pages/Credits';
import CreditDetail from 'pages/CreditDetail';
import HistoricalSATReport from './HistoricalSATReport';
import HistoricalACTReport from './HistoricalACTReport';
import HistoricalSHSATReport from './HistoricalSHSATReport';
import HistoricalBCAReport from './HistoricalBCAReport';
import Lectures from 'pages/Lectures';
import Vocab from 'pages/Vocab';
import VocabQuizReport from 'pages/VocabQuizReport';
import FillApp from 'pages/FillApp';
import FillAppReview from 'pages/FillAppReview';
import FillAppEssays from 'pages/FillAppEssays';
import FillAppEssayReview from 'pages/FillAppEssayReview';
import FillAppColleges from 'pages/FillAppColleges';

function AppRoutes() {
    const { user } = useAuthContext();

    useEffect(() => {
        if (user && user.organizations) {
            if (user.organizations[0].navbarHeight) {
                document.documentElement.style.setProperty('--logoHeight', `${user.organizations[0].navbarHeight}px`);
            }
            if (user.organizations[0].backgroundColor) {
                document.documentElement.style.setProperty('--backgroundColor', user.organizations[0].backgroundColor);
            }
        }
    }, [user]);

    if (!user) {
        return (
            <Routes>
                <Route path='/sign-in' element={<SignIn />} />
                <Route path='/password-reset' element={<PasswordReset />} />
                <Route path='*' element={<Navigate to='/sign-in' />} />
            </Routes>
        );
    }

    return (
        <Routes>
            <Route path='/' element={<Navigate to='/tests' />} />
            <Route path='/students' element={<Students />} />
            <Route path='/students/:studentId' element={<Student />} />
            {/* Bulk print: the selected tests ride in the query string
                (`?tests=<id>:<type>,...`) so the sheet survives a refresh. */}
            <Route path='/students/:studentId/print-reports' element={<BulkPrintReports />} />
            <Route path='/students/:studentId/digital-sat/performanceHistory' element={<HistoricalSATReport />} />
            <Route path='/students/:studentId/digital-act/performanceHistory' element={<HistoricalACTReport />} />
            <Route path='/students/:studentId/digital-shsat/performanceHistory' element={<HistoricalSHSATReport />} />
            <Route path='/students/:studentId/digital-bca/performanceHistory' element={<HistoricalBCAReport />} />
            <Route path='/latestScores' element={<LatestScores />} />
            <Route path='/tests' element={<Tests />} />
            <Route path='/tests/upload' element={<UploadTest />} />
            <Route path='/tests/digital-sat/:testId' element={<TestSATPreview />} />
            <Route path='/tests/digital-act/:testId' element={<TestACTPreview />} />
            <Route path='/tests/digital-shsat/:testId' element={<TestSHSATPreview />} />
            <Route path='/tests/paper-act/:testId' element={<TestPaperACTPreview />} />
            <Route path='/tests/digital-tsia2/:testId' element={<TestTSIA2Preview />} />
            <Route path='/tests/digital-bca/:testId' element={<TestBCAPreview />} />
            <Route path='/report/digital-sat/:testId' element={<TestSATReport />} />
            <Route path='/report/digital-act/:testId' element={<TestACTReport />} />
            <Route path='/report/digital-shsat/:testId' element={<TestSHSATReport />} />
            <Route path='/report/digital-tsia2/:testId' element={<TestTSIA2Report />} />
            <Route path='/report/digital-bca/:testId' element={<TestBCAReport />} />
            <Route path='/report/digital-sat/:testId' element={<TestSATReport />} />
            <Route path='/report/digital-act/:testId' element={<TestACTReport />} />
            <Route path='/report/paper-act/:testId' element={<TestPaperACTReport />} />
            <Route path='/report/assignment/:assignmentId' element={<AssignmentReport />} />
            <Route path='/review/digital-sat/:testId' element={<TestSATReview />} />
            <Route path='/review/digital-act/:testId' element={<TestACTReview />} />
            <Route path='/review/digital-shsat/:testId' element={<TestSHSATReview />} />
            <Route path='/review/digital-tsia2/:testId' element={<TestTSIA2Review />} />
            <Route path='/review/digital-bca/:testId' element={<TestBCAReview />} />
            <Route path='/review/paper-act/:testId' element={<TestPaperACTReview />} />
            <Route path='/review/assignment/:assignmentId' element={<AssignmentReview />} />
            <Route path='/assignments' element={<Assignments />} />
            <Route path='/assignments/create' element={<CreateAssignment />} />
            <Route path='/assignments/:assignmentId' element={<AssignmentPreview />} />
            {(user.role === 'Super Admin' || user.role === 'Staff' || user.role === 'Admin') && <>
                <Route path='/instructors' element={<Instructors />} />
                <Route path='/instructors/:instructorId' element={<Instructor />} />
                <Route path='/credits' element={<Credits />} />
                <Route path='/credits/:organization/:month' element={<CreditDetail />} />
            </>}
            {user.customizations.find(customization => customization === 'Video Lectures') && <>
                <Route path='/lectures' element={<Lectures />} />
            </>}
            {!user.customizations.find(customization => customization === 'Hide Vocab') && <>
                <Route path='/vocab' element={<Vocab />} />
                <Route path='/vocab/quiz/:quizId' element={<VocabQuizReport />} />
            </>}
            {user.customizations.find(customization => customization === 'FillApp Enabled') && <>
                <Route path='/college-apps' element={<FillApp />} />
                <Route path='/college-apps/review/:studentOrganizationId' element={<FillAppReview />} />
                <Route path='/college-apps/essays/:studentOrganizationId' element={<FillAppEssays />} />
                <Route path='/college-apps/essays/version/:versionId' element={<FillAppEssayReview />} />
                <Route path='/college-apps/colleges/:studentOrganizationId' element={<FillAppColleges />} />
            </>}
            <Route path='/classes' element={<Classes />} />
            <Route path='/classes/:classId' element={<Class />} />
            <Route path='/classes/digital-sat/:classId/:testId' element={<ClassSATReport />} />
            <Route path='/classes/digital-act/:classId/:testId' element={<ClassACTReport />} />
            <Route path='/classes/digital-shsat/:classId/:testId' element={<ClassSHSATReport />} />
            <Route path='/classes/digital-tsia2/:classId/:testId' element={<ClassTSIA2Report />} />
            <Route path='/classes/digital-bca/:classId/:testId' element={<ClassBCAReport />} />
            <Route path='/classes/paper-act/:classId/:testId' element={<ClassPaperACTReport />} />
            <Route path='/classes/:classId/assignment/:assignmentId' element={<ClassAssignmentReport />} />
            <Route path='/profile' element={<Profile />} />
            <Route path='/sign-in' element={<Navigate to='/' />} />
            <Route path='/password-reset' element={<Navigate to='/' />} />
        </Routes>
    );
}

export default AppRoutes;